import React, { useState } from "react";
import * as XLSX from "xlsx";
import { useGetStoreSalesQuery } from "../slices/storeSalesApiSlice";
import { useGetStoreExpensesQuery } from "../slices/storeExpensesApiSlice";
import { useGetStoreIncomesQuery } from "../slices/storeIncomesApiSlice";
import { useGetDepositsQuery } from "../slices/depositsApiSlice";
import { useGetStoreSuppliesQuery } from "../slices/storeSuppliesApiSlice";

export default function Balance() {
  const today = new Date().toISOString().split("T")[0];

  // Fetch data using RTK Query
  const { data: storeSalesData, isLoading: storeSalesLoading } = useGetStoreSalesQuery();
  const { data: storeExpensesData, isLoading: storeExpensesLoading } = useGetStoreExpensesQuery({
    startDate: today,
    endDate: today,
  });
  const { data: storeIncomesData, isLoading: storeIncomesLoading } = useGetStoreIncomesQuery({
    startDate: today,
    endDate: today,
  });
  const { data: depositsData, isLoading: depositsLoading } = useGetDepositsQuery({
    startDate: today,
    endDate: today,
  });
  const { data: storeSuppliesData, isLoading: storeSuppliesLoading } = useGetStoreSuppliesQuery();

  const [date1, setDate1] = useState(today);
  const [date2, setDate2] = useState(today);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState({
    totalStoreSales: 0,
    totalStoreExpenses: 0,
    totalStoreIncomes: 0,
    totalDeposit: 0,
    totalPurchases: 0,
    totalCashInHand: 0,
  });

  // Generate dates between the range
  const getDatesBetween = (startDate, endDate) => {
    const dates = [];
    let currentDate = new Date(startDate);
   
    while (currentDate <= new Date(endDate)) {
      dates.push(currentDate.toISOString().split("T")[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  // Calculate and generate report
  const calculateReport = () => {
    const dates = getDatesBetween(date1, date2);

    const result = dates.map((date) => {
      const netStoreSales = storeSalesData?.storeSales?.filter(
        (storeSale) => storeSale.date && new Date(storeSale.date).toISOString().split("T")[0] === date
      ).reduce((sum, storeSale) => sum + Number(storeSale.totalPrice || 0), 0) || 0;

      const netStoreExpenses = storeExpensesData?.storeExpenses?.filter(
        (storeExpense) => storeExpense.date && new Date(storeExpense.date).toISOString().split("T")[0] === date
      ).reduce((sum, storeExpense) => sum + Number(storeExpense.amount || 0), 0) || 0;
      
      const cashCollected = storeIncomesData?.storeIncomes?.filter(
        (storeIncome) => storeIncome.date && new Date(storeIncome.date).toISOString().split("T")[0] === date
      ).reduce((sum, storeIncome) => sum + Number(storeIncome.amount || 0), 0) || 0;

      const depositTotal = depositsData?.deposits?.filter(
        (deposit) => deposit.date && new Date(deposit.date).toISOString().split("T")[0] === date
      ).reduce((sum, deposit) => sum + Number(deposit.amount || 0), 0) || 0;

      const netBank = depositTotal > 0 ? depositTotal * -1 : depositTotal * +1;

      const purchases = storeSuppliesData?.storeSupplies?.filter(
        (storeSupply) => storeSupply.date && new Date(storeSupply.date).toISOString().split("T")[0] === date
      ).reduce((sum, storeSupply) => sum + Number(storeSupply.cardCost || 0), 0) * -1;

      const netCash = netStoreSales + netStoreExpenses;
      const difference = cashCollected - netCash;
      const cashInHand = netCash + netBank + purchases;

      return {
        date,
        netStoreSales,
        netStoreExpenses,
        netCash,
        cashCollected,
        difference,
        netBank,
        purchases,
        cashInHand,
      };
    });

    setReportData(result);
    calculateTotals(result);
  };

  // Calculate overall totals
  const calculateTotals = (data) => {
  const totalStoreSales = data.reduce((sum, row) => sum + (row.netStoreSales || 0), 0);
  const totalStoreExpenses = data.reduce((sum, row) => sum + (row.netStoreExpenses || 0), 0);
  const totalStoreIncomes = data.reduce((sum, row) => sum + (row.cashCollected || 0), 0);
  const totalDeposit = data.reduce((sum, row) => sum + (row.netBank || 0), 0);
  const totalPurchases = data.reduce((sum, row) => sum + (row.purchases || 0), 0);
  const totalCashInHand = data.reduce((sum, row) => sum + (row.cashInHand || 0), 0);
  const totalNetCash = totalStoreSales + totalStoreExpenses;
  const totalDifference = totalStoreIncomes - totalNetCash;

  setTotals({
    totalStoreSales: totalStoreSales.toFixed(2),
    totalStoreExpenses: totalStoreExpenses.toFixed(2),
    totalStoreIncomes: totalStoreIncomes.toFixed(2),
    totalDeposit: totalDeposit.toFixed(2),
    totalPurchases: totalPurchases.toFixed(2),
    totalCashInHand: totalCashInHand.toFixed(2),
    totalNetCash: totalNetCash.toFixed(2),
    totalDifference: totalDifference.toFixed(2),
  });
};

   // Export to Excel
  const exportToExcel = () => {
    const exportData = reportData.map((row) => ({
      Date: row.date,
      NetStoreSales: row.netStoreSales.toFixed(2),
      NetStoreExpenses: row.netStoreExpenses.toFixed(2),
      NetCash: row.netCash.toFixed(2),
      CashCollected: row.cashCollected.toFixed(2),
      Difference: row.difference.toFixed(2),
      NetBank: row.netBank.toFixed(2),
      Purchases: row.purchases.toFixed(2),
      CashInHand: row.cashInHand.toFixed(2),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Balance Report");
    XLSX.writeFile(workbook, "BalanceReport.xlsx");
  };

  return (
    <div className="balance-report">
      <h1>Balance Report</h1>

      <div className="date-range">
        <label>
          Start Date:
          <input type="date" value={date1} onChange={(e) => setDate1(e.target.value)} />
        </label>
        <label>
          End Date:
          <input type="date" value={date2} onChange={(e) => setDate2(e.target.value)} />
        </label>
      </div>

      <button onClick={calculateReport} className="calculate-button">
        Calculate Report
      </button>

      {storeSalesLoading || storeExpensesLoading || storeIncomesLoading || depositsLoading || storeSuppliesLoading ? (
        <div>Loading data...</div>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Net StoreSales</th>
                <th>Net StoreExpenses</th>
                <th>Net Cash</th>
                <th>Cash Collected</th>
                <th>Difference</th>
                <th>Net Bank</th>
                <th>Purchases</th>
                <th>Cash In Hand</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row) => (
                <tr key={row.date}>
                  <td>{row.date}</td>
                  <td>{row.netStoreSales.toFixed(2)}</td>
                  <td>{row.netStoreExpenses.toFixed(2)}</td>
                  <td>{row.netCash.toFixed(2)}</td>
                  <td>{row.cashCollected.toFixed(2)}</td>
                  <td>{row.difference.toFixed(2)}</td>
                  <td>{row.netBank.toFixed(2)}</td>
                  <td>{row.purchases.toFixed(2)}</td>
                  <td>{row.cashInHand.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
            <tr>
           <td><strong>Total</strong></td>
           <td><strong>{totals.totalStoreSales}</strong></td>
          <td><strong>{totals.totalStoreExpenses}</strong></td>
          <td><strong>{totals.totalNetCash}</strong></td>
          <td><strong>{totals.totalStoreIncomes}</strong></td>
          <td><strong>{totals.totalDifference}</strong></td>
          <td><strong>{totals.totalDeposit}</strong></td>
           <td><strong>{totals.totalPurchases}</strong></td>
           <td><strong>{totals.totalCashInHand}</strong></td>
          </tr>
          </tfoot>
          </table>
          <button onClick={exportToExcel} className="export-button">
            Export to Excel
          </button>
        </>
      )}
    </div>
  );
}